import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import { startSupportSession, clearSupportSession } from "@/components/SupportSessionBanner";

// ── Shared types ──────────────────────────────────────────────────────────────

interface UserResult {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  displayName: string;
  sites: {
    siteId: number;
    companyName: string;
    companyURL: string;
    isAdministrator: boolean;
    isAgent: boolean;
    isManager: boolean;
    isActive: boolean;
    hasLicense: boolean;
  }[];
}

interface SiteResult {
  siteId: number;
  companyName: string;
  companyURL: string;
  dateCreated: string | null;
  phoneNumber: string | null;
  city: string | null;
  region: string | null;
  isTestSite: boolean | null;
  admins: {
    userId: number;
    userName: string;
    firstName: string;
    lastName: string;
  }[];
}

interface AdminUser {
  userSiteId: number;
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  isAdministrator: boolean;
  isAgent: boolean;
  isManager: boolean;
  role: "SuperAdmin" | "Support";
}

interface SiteHealth {
  siteId: number;
  companyName: string;
  companyURL: string;
  isTestSite: boolean | null;
  dateCreated: string | null;
  subscription: { expirationDate: string | null; isActive: boolean } | null;
  activeAgentCount: number;
  lastLogin: { userEmail: string; loggedInAt: string; daysAgo: number } | null;
  last7Days: { activityActions: number; integrationErrors: number };
  features: Record<string, boolean> | null;
}

interface SiteAgent {
  userId: number;
  userName: string;
  firstName: string;
  lastName: string;
  isAdministrator: boolean;
  isManager: boolean;
  isAgent: boolean;
  isViewer: boolean;
  isActive: boolean;
  hasLicense: boolean;
  lastLogin: string | null;
}

interface ActivityEntry {
  id: number;
  userEmail: string;
  category: string | null;
  action: string | null;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  detail: string | null;
  createdAt: string;
}

interface IntegrationLogEntry {
  id: number;
  integration: string | null;
  severity: string | null;
  category: string | null;
  message: string | null;
  additionalInfo: string | null;
  suppressedCount: number;
  createdAt: string;
}

// ── Tab types ─────────────────────────────────────────────────────────────────

type Tab = "user-lookup" | "site-tools" | "velaro-staff" | "integration-logs";

export default function SupportTools() {
  const [tab, setTab] = useState<Tab>("user-lookup");

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold text-slate-900 mb-6">Support Tools</h1>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-6">
        {(
          [
            { id: "user-lookup", label: "User Lookup" },
            { id: "site-tools", label: "Site Tools" },
            { id: "velaro-staff", label: "Velaro Staff" },
            { id: "integration-logs", label: "Integration Logs" },
          ] as { id: Tab; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "user-lookup" && <UserLookupTab />}
      {tab === "site-tools" && <SiteToolsTab />}
      {tab === "velaro-staff" && <VelaroStaffTab />}
      {tab === "integration-logs" && <IntegrationLogsTab />}
    </div>
  );
}

// ── User Lookup ───────────────────────────────────────────────────────────────

function UserLookupTab() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<UserResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<UserResult | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [togglingActive, setTogglingActive] = useState<string | null>(null);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    setSelected(null);
    try {
      const data: UserResult[] = await (
        await api.admin.get(`SupportTools/users/search?q=${encodeURIComponent(q)}`)
      ).json();
      setResults(data);
    } catch (err) {
      setError(`Search failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  async function changeUsername() {
    if (!selected || !newEmail.trim()) return;
    setSaving(true);
    setSaveMsg("");
    try {
      await api.admin.post(`SupportTools/users/${selected.userId}/change-username`, { newEmail });
      setSaveMsg(`Username updated to ${newEmail}`);
      setSelected({ ...selected, userName: newEmail });
      setResults(results.map((r) => (r.userId === selected.userId ? { ...r, userName: newEmail } : r)));
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setSaveMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(userId: number, siteId: number, isActive: boolean) {
    const key = `${userId}-${siteId}`;
    setTogglingActive(key);
    try {
      await api.admin.post(`SupportTools/users/${userId}/sites/${siteId}/set-active`, { isActive: !isActive });
      if (selected?.userId === userId) {
        setSelected({
          ...selected,
          sites: selected.sites.map((s) => s.siteId === siteId ? { ...s, isActive: !isActive } : s),
        });
      }
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      alert(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setTogglingActive(null);
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email, first name, or last name"
          className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {results.length > 0 && (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Sites</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((u) => (
                <tr key={u.userId} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-400">{u.userId}</td>
                  <td className="px-4 py-2 font-mono text-xs">{u.userName}</td>
                  <td className="px-4 py-2">{u.firstName} {u.lastName}</td>
                  <td className="px-4 py-2 text-slate-500">{u.sites.length} site{u.sites.length !== 1 ? "s" : ""}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => { setSelected(u); setNewEmail(u.userName); setSaveMsg(""); }}
                      className="text-[var(--color-primary)] text-xs hover:underline"
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="border border-slate-200 rounded p-4 space-y-4 bg-slate-50">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-slate-900">{selected.firstName} {selected.lastName}</p>
              <p className="text-sm text-slate-500 font-mono">{selected.userName}</p>
              <p className="text-xs text-slate-400">User ID: {selected.userId}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600 text-xs">✕ Close</button>
          </div>

          {selected.sites.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Sites</p>
              <div className="space-y-1">
                {selected.sites.map((s) => (
                  <div key={s.siteId} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-xs text-slate-400 w-14">{s.siteId}</span>
                    <span className="flex-1">{s.companyName}</span>
                    <span className="text-xs text-slate-400">{s.companyURL}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.isAdministrator ? "bg-blue-100 text-blue-700" : s.isManager ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>
                      {s.isAdministrator ? "Admin" : s.isManager ? "Manager" : "Agent"}
                    </span>
                    <button
                      onClick={() => toggleActive(selected.userId, s.siteId, s.isActive)}
                      disabled={togglingActive === `${selected.userId}-${s.siteId}`}
                      className={`text-xs px-2 py-0.5 rounded-full border disabled:opacity-50 ${s.isActive ? "border-green-300 text-green-700 hover:bg-red-50 hover:border-red-300 hover:text-red-600" : "border-red-300 text-red-600 hover:bg-green-50 hover:border-green-300 hover:text-green-700"}`}
                    >
                      {togglingActive === `${selected.userId}-${s.siteId}` ? "…" : s.isActive ? "Active" : "Inactive"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Change Username / Email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <button
                onClick={changeUsername}
                disabled={saving || newEmail === selected.userName}
                className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
              >
                {saving ? "Saving…" : "Update"}
              </button>
            </div>
            {saveMsg && <p className={`text-sm mt-1 ${saveMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{saveMsg}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Site Tools ────────────────────────────────────────────────────────────────

type SiteSubTab = "health" | "agents" | "activity";

function SiteToolsTab() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SiteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSite, setSelectedSite] = useState<SiteResult | null>(null);
  const [subTab, setSubTab] = useState<SiteSubTab>("health");

  async function search(e: React.FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    try {
      const data: SiteResult[] = await (
        await api.admin.get(`SupportTools/sites/search?q=${encodeURIComponent(q)}`)
      ).json();
      setResults(data);
      if (data.length === 1) setSelectedSite(data[0]);
    } catch (err) {
      setError(`Search failed: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Site ID, company name, or URL"
          className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
        <button type="submit" disabled={loading} className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50">
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {results.length > 1 && !selectedSite && (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Site ID</th>
                <th className="px-4 py-2 text-left">Company</th>
                <th className="px-4 py-2 text-left">URL</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((s) => (
                <tr key={s.siteId} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs text-slate-400">{s.siteId}</td>
                  <td className="px-4 py-2 font-medium">{s.companyName}{s.isTestSite && <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">Test</span>}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{s.companyURL}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setSelectedSite(s)} className="text-[var(--color-primary)] text-xs hover:underline">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedSite && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-lg font-semibold text-slate-900">{selectedSite.companyName}</span>
              <span className="ml-2 text-sm text-slate-400 font-mono">#{selectedSite.siteId}</span>
              {selectedSite.isTestSite && <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">Test</span>}
            </div>
            <button onClick={() => { setSelectedSite(null); setResults([]); }} className="text-slate-400 hover:text-slate-600 text-xs">← Back to search</button>
          </div>

          <div className="flex gap-1 border-b border-slate-200">
            {(["health", "agents", "activity"] as SiteSubTab[]).map((t) => (
              <button key={t} onClick={() => setSubTab(t)} className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${subTab === t ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                {t === "health" ? "Health & Actions" : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {subTab === "health" && <SiteHealthTab siteId={selectedSite.siteId} />}
          {subTab === "agents" && <SiteAgentsTab siteId={selectedSite.siteId} />}
          {subTab === "activity" && <SiteActivityTab siteId={selectedSite.siteId} />}
        </div>
      )}
    </div>
  );
}

function SiteHealthTab({ siteId }: { siteId: number }) {
  const api = useApi();
  const [health, setHealth] = useState<SiteHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [extendDays, setExtendDays] = useState(30);
  const [extending, setExtending] = useState(false);
  const [extendMsg, setExtendMsg] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateMsg, setImpersonateMsg] = useState("");
  const [togglingFlag, setTogglingFlag] = useState<string | null>(null);
  const [flagMsg, setFlagMsg] = useState("");

  React.useEffect(() => {
    load();
  }, [siteId]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data: SiteHealth = await (await api.admin.get(`SupportTools/sites/${siteId}/health`)).json();
      setHealth(data);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  async function extendSubscription() {
    setExtending(true);
    setExtendMsg("");
    try {
      const res = await api.admin.post(`SupportTools/sites/${siteId}/extend-subscription`, { days: extendDays });
      const data = await res.json();
      setExtendMsg(`Extended to ${new Date(data.newExpiration).toLocaleDateString()}`);
      await load();
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setExtendMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setExtending(false);
    }
  }

  async function impersonate() {
    setImpersonating(true);
    setImpersonateMsg("");
    try {
      const res = await api.admin.post(`SupportTools/sites/${siteId}/impersonate`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setImpersonateMsg(`Error: ${body?.error ?? res.status}`);
        return;
      }
      const data = await res.json();
      startSupportSession({
        token: data.token,
        targetSiteId: data.targetSiteId,
        targetSiteName: data.targetSiteName,
        expiresAt: data.expiresAt,
      });
      setImpersonateMsg(`Session started. You are now viewing as ${data.targetSiteName}. A banner will appear at the top of every page.`);
    } catch (err) {
      setImpersonateMsg(`Error: ${String(err)}`);
    } finally {
      setImpersonating(false);
    }
  }

  async function toggleFlag(flag: string, currentValue: boolean) {
    setTogglingFlag(flag);
    setFlagMsg("");
    try {
      await api.admin.post(`SupportTools/sites/${siteId}/features/${flag}`, { enabled: !currentValue });
      setFlagMsg(`${flag} → ${!currentValue}`);
      await load();
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setFlagMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setTogglingFlag(null);
    }
  }

  if (loading) return <p className="text-sm text-slate-400">Loading health snapshot…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!health) return null;

  const subExpired = health.subscription && !health.subscription.isActive;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <HealthCard
          label="Subscription"
          value={health.subscription ? (health.subscription.isActive ? "Active" : "Expired") : "None"}
          sub={health.subscription?.expirationDate ? new Date(health.subscription.expirationDate).toLocaleDateString() : undefined}
          alert={subExpired ?? false}
        />
        <HealthCard label="Active Agents" value={String(health.activeAgentCount)} />
        <HealthCard
          label="Last Login"
          value={health.lastLogin ? `${health.lastLogin.daysAgo}d ago` : "Never"}
          sub={health.lastLogin?.userEmail}
        />
        <HealthCard
          label="7d Activity"
          value={String(health.last7Days.activityActions)}
          sub={`${health.last7Days.integrationErrors} integration errors`}
          alert={health.last7Days.integrationErrors > 0}
        />
      </div>

      {/* Impersonation */}
      <div className="border border-slate-200 rounded p-4 space-y-2">
        <p className="text-sm font-semibold text-slate-700">Start Support Session (SuperAdmin only)</p>
        <p className="text-xs text-slate-500">Creates a 15-minute token that routes all your API calls to this site. Shown in the top banner on every page.</p>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            onClick={impersonate}
            disabled={impersonating}
            className="px-4 py-2 bg-amber-500 text-white rounded text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
          >
            {impersonating ? "Starting…" : "Start Session as This Site"}
          </button>
          <button
            onClick={clearSupportSession}
            className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-600 hover:bg-slate-50"
          >
            Clear Session
          </button>
        </div>
        {impersonateMsg && <p className={`text-xs mt-1 ${impersonateMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{impersonateMsg}</p>}
      </div>

      {/* Extend subscription */}
      <div className="border border-slate-200 rounded p-4 space-y-2">
        <p className="text-sm font-semibold text-slate-700">Extend Subscription (SuperAdmin only)</p>
        <div className="flex gap-2 items-center">
          <input
            type="number"
            value={extendDays}
            onChange={(e) => setExtendDays(Number(e.target.value))}
            min={1}
            max={365}
            className="w-24 border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <span className="text-sm text-slate-500">days</span>
          <button
            onClick={extendSubscription}
            disabled={extending}
            className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {extending ? "Extending…" : "Extend"}
          </button>
        </div>
        {extendMsg && <p className={`text-xs mt-1 ${extendMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{extendMsg}</p>}
      </div>

      {/* Feature flags */}
      {health.features && (
        <div className="border border-slate-200 rounded p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-700">Feature Flags (SuperAdmin only)</p>
          {flagMsg && <p className={`text-xs ${flagMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{flagMsg}</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Object.entries(health.features).map(([flag, enabled]) => (
              <button
                key={flag}
                onClick={() => toggleFlag(flag, enabled)}
                disabled={togglingFlag === flag}
                className={`flex items-center justify-between px-3 py-2 rounded border text-xs font-medium transition-colors disabled:opacity-50 ${enabled ? "border-green-300 bg-green-50 text-green-700 hover:bg-red-50 hover:border-red-300 hover:text-red-600" : "border-slate-200 bg-slate-50 text-slate-400 hover:bg-green-50 hover:border-green-300 hover:text-green-700"}`}
              >
                <span>{flag.replace(/^Enable/, "")}</span>
                <span>{togglingFlag === flag ? "…" : enabled ? "ON" : "OFF"}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HealthCard({ label, value, sub, alert }: { label: string; value: string; sub?: string; alert?: boolean }) {
  return (
    <div className={`border rounded p-3 space-y-1 ${alert ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}>
      <p className="text-xs text-slate-400 uppercase font-medium">{label}</p>
      <p className={`text-lg font-semibold ${alert ? "text-red-600" : "text-slate-900"}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 truncate">{sub}</p>}
    </div>
  );
}

function SiteAgentsTab({ siteId }: { siteId: number }) {
  const api = useApi();
  const [agents, setAgents] = useState<SiteAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data: SiteAgent[] = await (await api.admin.get(`SupportTools/sites/${siteId}/agents`)).json();
        setAgents(data);
      } catch (err) {
        setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId]);

  if (loading) return <p className="text-sm text-slate-400">Loading agents…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="border border-slate-200 rounded overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
          <tr>
            <th className="px-4 py-2 text-left">Email</th>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Role</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Last Login</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {agents.map((a) => (
            <tr key={a.userId} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-mono text-xs">{a.userName}</td>
              <td className="px-4 py-2">{a.firstName} {a.lastName}</td>
              <td className="px-4 py-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.isAdministrator ? "bg-blue-100 text-blue-700" : a.isAgent ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>
                  {a.isAdministrator ? "Admin" : a.isManager ? "Manager" : a.isAgent ? "Agent" : "Viewer"}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${a.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {a.isActive ? "Active" : "Inactive"}
                </span>
              </td>
              <td className="px-4 py-2 text-xs text-slate-500">
                {a.lastLogin ? new Date(a.lastLogin).toLocaleDateString() : "Never"}
              </td>
            </tr>
          ))}
          {agents.length === 0 && (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No agents found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SiteActivityTab({ siteId }: { siteId: number }) {
  const api = useApi();
  const [items, setItems] = useState<ActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    load(1);
  }, [siteId, category]);

  async function load(pg: number) {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: "50" });
      if (category) params.set("category", category);
      const data = await (await api.admin.get(`SupportTools/sites/${siteId}/activity?${params}`)).json();
      setItems(data.items);
      setTotal(data.total);
      setCategories(data.categories ?? []);
      setPage(pg);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-slate-400">{total.toLocaleString()} entries</span>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
            <tr>
              <th className="px-4 py-2 text-left">When</th>
              <th className="px-4 py-2 text-left">Who</th>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Action</th>
              <th className="px-4 py-2 text-left">Entity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="px-4 py-4 text-center text-slate-400 text-sm">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No activity found.</td></tr>}
            {items.map((entry) => (
              <tr key={entry.id} className="hover:bg-slate-50 align-top">
                <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs font-mono text-slate-500 max-w-[160px] truncate">{entry.userEmail}</td>
                <td className="px-4 py-2 text-xs text-slate-400">{entry.category ?? "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-700">{entry.action ?? "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">
                  {entry.entityName ?? entry.entityId ?? "—"}
                  {entry.detail && <div className="text-slate-400 mt-0.5 font-mono">{entry.detail}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => load(page - 1)} disabled={page === 1 || loading} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">← Prev</button>
            <button onClick={() => load(page + 1)} disabled={page === totalPages || loading} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Velaro Staff ──────────────────────────────────────────────────────────────

function VelaroStaffTab() {
  const api = useApi();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addRole, setAddRole] = useState<"Support" | "SuperAdmin">("Support");
  const [adding, setAdding] = useState(false);
  const [addMsg, setAddMsg] = useState("");
  const [removing, setRemoving] = useState<number | null>(null);
  const [changingRole, setChangingRole] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data: AdminUser[] = await (await api.admin.get("SupportTools/admin-users")).json();
      setUsers(data);
      setLoaded(true);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  async function addUser(e: React.FormEvent) {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    setAddMsg("");
    try {
      await api.admin.post("SupportTools/admin-users", { email: addEmail, role: addRole });
      setAddMsg(`${addEmail} added as ${addRole}.`);
      setAddEmail("");
      await load();
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      setAddMsg(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setAdding(false);
    }
  }

  async function changeRole(userId: number, newRole: "SuperAdmin" | "Support") {
    setChangingRole(userId);
    try {
      await api.admin.patch(`SupportTools/admin-users/${userId}/role`, { role: newRole });
      setUsers(users.map((u) => u.userId === userId ? { ...u, role: newRole, isManager: newRole === "SuperAdmin" } : u));
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      alert(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setChangingRole(null);
    }
  }

  async function removeUser(userId: number, email: string) {
    if (!confirm(`Remove admin access for ${email}?`)) return;
    setRemoving(userId);
    try {
      await api.admin.delete(`SupportTools/admin-users/${userId}`);
      setUsers(users.filter((u) => u.userId !== userId));
    } catch (err) {
      const body = err instanceof Response ? await err.json().catch(() => ({})) : {};
      alert(`Error: ${body?.error ?? String(err)}`);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Manage Velaro staff access (site 1032). <strong>SuperAdmin</strong> — impersonation + destructive ops. <strong>Support</strong> — read-only diagnostics.
      </p>

      {!loaded && (
        <button onClick={load} disabled={loading} className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50">
          {loading ? "Loading…" : "Load Staff"}
        </button>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {loaded && (
        <>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.userId} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-xs">{u.userName}</td>
                    <td className="px-4 py-2">{u.firstName} {u.lastName}</td>
                    <td className="px-4 py-2">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u.userId, e.target.value as "SuperAdmin" | "Support")}
                        disabled={changingRole === u.userId}
                        className={`text-xs px-2 py-1 rounded border font-medium disabled:opacity-50 ${u.role === "SuperAdmin" ? "border-purple-300 bg-purple-50 text-purple-700" : "border-blue-200 bg-blue-50 text-blue-700"}`}
                      >
                        <option value="SuperAdmin">SuperAdmin</option>
                        <option value="Support">Support</option>
                      </select>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => removeUser(u.userId, u.userName)}
                        disabled={removing === u.userId}
                        className="text-red-500 text-xs hover:underline disabled:opacity-50"
                      >
                        {removing === u.userId ? "Removing…" : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400 text-sm">No staff found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Add Staff Member</p>
            <form onSubmit={addUser} className="flex flex-wrap gap-2">
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="velaro email address"
                className="flex-1 min-w-[200px] border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
              <select
                value={addRole}
                onChange={(e) => setAddRole(e.target.value as "SuperAdmin" | "Support")}
                className="border border-slate-300 rounded px-3 py-2 text-sm"
              >
                <option value="Support">Support</option>
                <option value="SuperAdmin">SuperAdmin</option>
              </select>
              <button type="submit" disabled={adding || !addEmail.trim()} className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50">
                {adding ? "Adding…" : "Add"}
              </button>
            </form>
            {addMsg && <p className={`text-sm mt-1 ${addMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{addMsg}</p>}
          </div>
        </>
      )}
    </div>
  );
}

// ── Integration Logs ──────────────────────────────────────────────────────────

interface IntegrationLogsResponse {
  items: IntegrationLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  integrations: string[];
}

const SEVERITY_COLORS: Record<string, string> = {
  Error:   "bg-red-100 text-red-700",
  Warning: "bg-yellow-100 text-yellow-700",
  Info:    "bg-blue-100 text-blue-700",
  Debug:   "bg-slate-100 text-slate-600",
};

function IntegrationLogsTab() {
  const api = useApi();
  const [siteIdInput, setSiteIdInput] = useState("");
  const [siteId, setSiteId] = useState<number | null>(null);
  const [integration, setIntegration] = useState("");
  const [severity, setSeverity] = useState("All");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<IntegrationLogsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load(targetSiteId: number, pg = 1) {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(pg), pageSize: "50" });
      if (integration) params.set("integration", integration);
      if (severity && severity !== "All") params.set("severity", severity);
      const result: IntegrationLogsResponse = await (
        await api.admin.get(`SupportTools/sites/${targetSiteId}/integration-logs?${params}`)
      ).json();
      setData(result);
      setPage(pg);
    } catch (err) {
      setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const id = parseInt(siteIdInput.trim(), 10);
    if (!id || isNaN(id)) { setError("Enter a valid numeric site ID."); return; }
    setSiteId(id);
    load(id, 1);
  }

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">View integration event logs for any customer site.</p>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Site ID</label>
          <input type="number" value={siteIdInput} onChange={(e) => setSiteIdInput(e.target.value)} placeholder="e.g. 1234" className="border border-slate-300 rounded px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]" />
        </div>
        {data?.integrations && data.integrations.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-medium">Integration</label>
            <select value={integration} onChange={(e) => setIntegration(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm">
              <option value="">All</option>
              {data.integrations.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Severity</label>
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="border border-slate-300 rounded px-3 py-2 text-sm">
            <option value="All">All</option>
            <option value="Error">Error</option>
            <option value="Warning">Warning</option>
            <option value="Info">Info</option>
            <option value="Debug">Debug</option>
          </select>
        </div>
        <button type="submit" disabled={loading} className="px-4 py-2 bg-[var(--color-primary)] text-white rounded text-sm font-medium disabled:opacity-50 self-end">
          {loading ? "Loading…" : "Load Logs"}
        </button>
        {siteId && data && (
          <button type="button" onClick={() => load(siteId, page)} disabled={loading} className="px-3 py-2 border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 self-end hover:bg-slate-50">
            Refresh
          </button>
        )}
      </form>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {data && (
        <>
          <div className="text-xs text-slate-400">{data.total.toLocaleString()} entries for site {siteId}</div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">When</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-left">Integration</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No entries found.</td></tr>}
                {data.items.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50 align-top">
                    <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">{new Date(entry.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2">
                      {entry.severity && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEVERITY_COLORS[entry.severity] ?? "bg-slate-100 text-slate-600"}`}>{entry.severity}</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{entry.integration ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-slate-400">{entry.category ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-slate-700">
                      <div>{entry.message}</div>
                      {entry.additionalInfo && <div className="text-slate-400 mt-0.5">{entry.additionalInfo}</div>}
                      {entry.suppressedCount > 0 && <div className="text-slate-400 mt-0.5">+{entry.suppressedCount} suppressed</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => siteId && load(siteId, page - 1)} disabled={page === 1 || loading} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">← Prev</button>
                <button onClick={() => siteId && load(siteId, page + 1)} disabled={page === totalPages || loading} className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-slate-50">Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
